import { useRef, useState } from 'react'
import { CheckCircle2, FileSignature, FolderOpen, Loader2, Trash2, X, XCircle } from 'lucide-react'

import { useAppStore } from '@core/store'
import { downloadBlob } from '@core/lib/downloadBlob'
import { rawIfc } from '@viewer/lib/rawIfc'
import { schemaLabel } from '@viewer/lib/ifcSchema'
import { AVISO, BTN, BTN_PRIMARIO, ERRO, INPUT, NOTA, OK, ROTULO } from '@viewer/dados/estilos'
import {
  criarManifesto,
  gerarTermoPdf,
  tamanhoLegivel,
  type DadosDoTermo,
  type FicheiroEntregue,
  type ManifestoDeEntrega,
} from './lib/termoDeEntrega'
import {
  assinaturasDoPdf,
  compararFicheiros,
  conferirReferencias,
  extrairManifesto,
  lerContentorAsic,
  sha256Hex,
  termoNoContentor,
  type AssinaturaPdf,
  type ContentorAsic,
  type EstadoDoFicheiro,
  type LinhaDaComparacao,
} from './lib/lerEntrega'

/** Entrega assinada.
 *
 *  Termo de entrega: um PDF que identifica cada ficheiro pela impressão
 *  digital SHA-256, para assinar no Autenticação.gov (Cartão de Cidadão
 *  ou Chave Móvel Digital). Os IFC não são alterados.
 *
 *  Verificar entrega: o termo assinado (ou um contentor ASiC) e os
 *  ficheiros recebidos. Confere-se a integridade e a correspondência dos
 *  ficheiros; a validade do certificado confirma-se no Autenticação.gov
 *  ou no validador da Comissão Europeia. */
export default function EntregaAssinadaPanel({ aoFechar }: { aoFechar: () => void }) {
  const [separador, setSeparador] = useState<'termo' | 'verificar'>('termo')
  const sep = (id: typeof separador, rotulo: string) => (
    <button
      type="button"
      onClick={() => setSeparador(id)}
      aria-pressed={separador === id}
      className={`rounded px-2 py-0.5 text-[11px] ${
        separador === id ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
      }`}
    >
      {rotulo}
    </button>
  )
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1 border-b border-[var(--color-border)] px-2 py-1">
        <FileSignature size={13} className="text-[var(--color-accent)]" />
        <span className="text-xs font-medium text-[var(--color-text)]">Entrega assinada</span>
        <span className="ml-2 flex gap-1">
          {sep('termo', 'Termo de entrega')}
          {sep('verificar', 'Verificar entrega')}
        </span>
        <button type="button" onClick={aoFechar} className="ml-auto rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]" title="Fechar">
          <X size={14} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-3 text-[11px]">
        {separador === 'termo' ? <TermoDeEntrega /> : <VerificarEntrega />}
      </div>
    </div>
  )
}

function hoje(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function nomeDeFicheiroSeguro(t: string): string {
  return t.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_').slice(0, 80) || 'entrega'
}

async function ficheirosDoDisco(lista: FileList | null, aoProgredir: (n: number, total: number) => void): Promise<FicheiroEntregue[]> {
  const out: FicheiroEntregue[] = []
  const fs = Array.from(lista ?? [])
  for (let i = 0; i < fs.length; i++) {
    aoProgredir(i, fs.length)
    const f = fs[i]
    out.push({ nome: f.name, tamanho: f.size, sha256: await sha256Hex(await f.arrayBuffer()) })
  }
  return out
}

// ---------------------------------------------------------------------
// Termo de entrega
// ---------------------------------------------------------------------

function TermoDeEntrega() {
  const models = useAppStore((s) => s.models)
  const discoRef = useRef<HTMLInputElement>(null)
  const [dados, setDados] = useState<DadosDoTermo>({ projecto: '', entrega: '', emitidoPor: '', destinatario: '', data: hoje(), observacoes: '' })
  const [ficheiros, setFicheiros] = useState<FicheiroEntregue[]>([])
  const [avisos, setAvisos] = useState<string[]>([])
  const [aLer, setALer] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const juntar = (novos: FicheiroEntregue[]) =>
    setFicheiros((actuais) => [...actuais, ...novos.filter((n) => !actuais.some((a) => a.sha256 === n.sha256 && a.nome === n.nome))])

  const dosModelosAbertos = () => {
    const novos: FicheiroEntregue[] = []
    const av: string[] = []
    for (const m of models) {
      const bytes = rawIfc.getCachedBytes(m.id)
      if (m.sourceFormat === 'frag' || !bytes) {
        av.push(`${m.fileName}: aberto de um .frag ou sem o IFC em memória; junta o .ifc a partir do disco.`)
        continue
      }
      if (/\.ifczip$/i.test(m.fileName)) {
        av.push(`${m.fileName}: a impressão digital é a do IFC extraído do .ifczip, não a do .ifczip. Se a entrega for o .ifczip, junta-o a partir do disco.`)
      }
      novos.push({ nome: m.fileName, tamanho: bytes.byteLength, sha256: m.sha256.toLowerCase(), esquema: m.schemaVersion ? schemaLabel(m.schemaVersion) : undefined })
    }
    juntar(novos)
    setAvisos(av)
  }

  const doDisco = async (lista: FileList | null) => {
    setErro(null)
    try {
      juntar(await ficheirosDoDisco(lista, (n, t) => setALer(`A calcular a impressão digital: ${n + 1} de ${t}…`)))
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setALer(null)
      if (discoRef.current) discoRef.current.value = ''
    }
  }

  const gerar = async () => {
    setErro(null)
    try {
      const m = criarManifesto(dados, ficheiros)
      const pdf = await gerarTermoPdf(m)
      downloadBlob(new Uint8Array(pdf), `Termo_de_entrega_${nomeDeFicheiroSeguro(dados.projecto)}_${dados.data}.pdf`, 'application/pdf')
      useAppStore.getState().pushToast('Termo de entrega gerado. Assinar na aplicação Autenticação.gov.', { type: 'success' })
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    }
  }

  const campo = (k: keyof DadosDoTermo, rotulo: string, tipo: 'text' | 'date' = 'text') => (
    <label className="flex flex-col gap-0.5">
      <span className={ROTULO}>{rotulo}</span>
      <input type={tipo} value={dados[k] ?? ''} onChange={(e) => setDados({ ...dados, [k]: e.target.value })} className={INPUT} />
    </label>
  )

  return (
    <>
      <p className={NOTA}>
        O termo identifica cada ficheiro pela impressão digital SHA-256. Assina-se o PDF na aplicação Autenticação.gov, com o
        Cartão de Cidadão ou a Chave Móvel Digital (assinatura electrónica qualificada), e envia-se junto com os ficheiros.
        Os ficheiros não são alterados: continuam a abrir e a poder ser editados, e qualquer alteração passa a ser detectável.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {campo('projecto', 'Projecto')}
        {campo('entrega', 'Entrega (fase, revisão)')}
        {campo('emitidoPor', 'Emitido por')}
        {campo('destinatario', 'Destinatário')}
        {campo('data', 'Data', 'date')}
        {campo('observacoes', 'Observações')}
      </div>

      <div className="flex items-center gap-1">
        <span className={ROTULO}>Ficheiros ({ficheiros.length})</span>
        <span className="ml-auto flex gap-1">
          <button type="button" onClick={dosModelosAbertos} disabled={models.length === 0} className={BTN} title="Juntar os modelos abertos no viewer">
            Modelos abertos
          </button>
          <button type="button" onClick={() => discoRef.current?.click()} className={BTN} title="Juntar ficheiros do disco (qualquer tipo)">
            <FolderOpen size={11} /> Do disco
          </button>
        </span>
        <input ref={discoRef} type="file" multiple className="hidden" onChange={(e) => void doDisco(e.target.files)} />
      </div>
      {aLer && (
        <p className={NOTA}>
          <Loader2 size={10} className="mr-1 inline animate-spin" />
          {aLer}
        </p>
      )}
      {avisos.map((a) => (
        <p key={a} className={AVISO}>
          {a}
        </p>
      ))}
      {ficheiros.length === 0 ? (
        <p className={NOTA}>Nenhum ficheiro. Juntar os modelos abertos ou ficheiros do disco (IFC, PDF, BCF ou outros).</p>
      ) : (
        <ul className="flex flex-col gap-1" data-campo="ficheiros-do-termo">
          {ficheiros.map((f, i) => (
            <li key={`${f.nome}-${f.sha256}`} className="rounded border border-[var(--color-border)] px-2 py-1">
              <div className="flex items-center gap-1">
                <span className="min-w-0 flex-1 truncate text-[var(--color-text)]" title={f.nome}>
                  {f.nome}
                </span>
                <span className="shrink-0 text-[var(--color-text-dim)]">
                  {tamanhoLegivel(f.tamanho)}
                  {f.esquema ? ` · ${f.esquema}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setFicheiros(ficheiros.filter((_, k) => k !== i))}
                  className="rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-error-text)]"
                  title="Tirar da lista"
                >
                  <Trash2 size={11} />
                </button>
              </div>
              <div className="break-all font-mono text-[9.5px] text-[var(--color-text-dim)]">SHA-256 {f.sha256}</div>
            </li>
          ))}
        </ul>
      )}
      {erro && <p className={ERRO}>{erro}</p>}
      <div>
        <button type="button" onClick={() => void gerar()} disabled={ficheiros.length === 0 || !dados.projecto.trim()} className={BTN_PRIMARIO}>
          <FileSignature size={11} /> Gerar termo (PDF)
        </button>
        {!dados.projecto.trim() && ficheiros.length > 0 && <span className={`${NOTA} ml-2`}>Indicar o projecto.</span>}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------
// Verificar entrega
// ---------------------------------------------------------------------

const ROTULO_DO_ESTADO: Record<EstadoDoFicheiro, string> = {
  igual: 'Confere',
  'igual-outro-nome': 'Confere (com outro nome)',
  diferente: 'Diferente do assinado',
  'em-falta': 'Em falta',
}

interface Resultado {
  origem: 'pdf' | 'asic'
  nome: string
  manifesto: ManifestoDeEntrega | null
  manifestoAssinado: boolean | null
  assinaturasPdf: AssinaturaPdf[]
  asic: ContentorAsic | null
  referenciasAsic: Array<{ nome: string; estado: 'confere' | 'diferente' | 'nao-referido' }>
  comparacao: { linhas: LinhaDaComparacao[]; aMais: Array<{ nome: string; sha256: string }> } | null
}

function VerificarEntrega() {
  const models = useAppStore((s) => s.models)
  const termoRef = useRef<HTMLInputElement>(null)
  const recebidosRef = useRef<HTMLInputElement>(null)
  const [termo, setTermo] = useState<File | null>(null)
  const [recebidos, setRecebidos] = useState<Array<{ nome: string; sha256: string }>>([])
  const [usarAbertos, setUsarAbertos] = useState(true)
  const [aLer, setALer] = useState<string | null>(null)
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const escolherRecebidos = async (lista: FileList | null) => {
    setErro(null)
    try {
      const fs = await ficheirosDoDisco(lista, (n, t) => setALer(`A calcular a impressão digital: ${n + 1} de ${t}…`))
      setRecebidos((r) => [...r, ...fs.map((f) => ({ nome: f.nome, sha256: f.sha256 }))])
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setALer(null)
      if (recebidosRef.current) recebidosRef.current.value = ''
    }
  }

  const verificar = async () => {
    if (!termo) return
    setErro(null)
    setResultado(null)
    setALer('A ler…')
    try {
      const bytes = new Uint8Array(await termo.arrayBuffer())
      const abertos = usarAbertos
        ? models.filter((m) => m.sourceFormat !== 'frag').map((m) => ({ nome: m.fileName, sha256: m.sha256.toLowerCase() }))
        : []
      const todos = [...recebidos, ...abertos.filter((a) => !recebidos.some((r) => r.sha256 === a.sha256))]
      const ePdf = bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46
      if (ePdf) {
        const m = extrairManifesto(bytes)
        const assinaturas = await assinaturasDoPdf(bytes)
        const primeira = assinaturas[0]
        setResultado({
          origem: 'pdf',
          nome: termo.name,
          manifesto: m?.manifesto ?? null,
          manifestoAssinado: m && primeira ? m.posicao < primeira.byteRange[1] : null,
          assinaturasPdf: assinaturas,
          asic: null,
          referenciasAsic: [],
          comparacao: m ? compararFicheiros(m.manifesto.ficheiros, todos) : null,
        })
      } else if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
        const c = await lerContentorAsic(bytes)
        const refs = await conferirReferencias(c)
        // Com um termo do hub dentro, a lista dele compara-se com os
        // ficheiros do contentor (e com os recebidos, se houver). Sem
        // termo, os ficheiros do contentor são a própria lista.
        const t = termoNoContentor(c)
        // O próprio termo não se compara consigo mesmo: não está na lista
        // que ele próprio declara, e aparecia como "fora da entrega".
        const doContentor = c.ficheiros.filter((f) => f.nome !== t?.nome).map((f) => ({ nome: f.nome, sha256: f.sha256 }))
        const recebidosEContentor = [...doContentor, ...todos.filter((x) => !doContentor.some((d) => d.sha256 === x.sha256))]
        setResultado({
          origem: 'asic',
          nome: termo.name,
          manifesto: t?.manifesto ?? null,
          manifestoAssinado: t ? refs.some((x) => x.nome === t.nome && x.estado === 'confere') : null,
          assinaturasPdf: [],
          asic: c,
          referenciasAsic: refs,
          comparacao: t
            ? compararFicheiros(t.manifesto.ficheiros, recebidosEContentor)
            : todos.length > 0
              ? compararFicheiros(c.ficheiros, todos)
              : null,
        })
      } else {
        throw new Error('O ficheiro não é um PDF nem um contentor ASiC (zip).')
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setALer(null)
    }
  }

  return (
    <>
      <p className={NOTA}>
        Confere a integridade do que foi assinado e se os ficheiros recebidos são exactamente os que constam da entrega. A
        validade do certificado (qualificado, dentro do prazo e não revogado) confirma-se na aplicação Autenticação.gov, no{' '}
        <a href="https://validador.autenticacao.gov.pt/validation" target="_blank" rel="noreferrer" className="underline">
          validador do Autenticação.gov
        </a>{' '}
        ou no{' '}
        <a href="https://ec.europa.eu/digital-building-blocks/DSS/webapp-demo/validation" target="_blank" rel="noreferrer" className="underline">
          validador da Comissão Europeia
        </a>
        .
      </p>
      <div className="flex flex-col gap-1">
        <span className={ROTULO}>Termo assinado (PDF) ou contentor ASiC</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => termoRef.current?.click()} className={BTN}>
            <FolderOpen size={11} /> Escolher
          </button>
          <span className="truncate text-[var(--color-text-muted)]">{termo?.name ?? 'nenhum'}</span>
        </div>
        <input ref={termoRef} type="file" accept=".pdf,.asice,.asics,.sce,.scs,.zip" className="hidden" onChange={(e) => setTermo(e.target.files?.[0] ?? null)} />
      </div>
      <div className="flex flex-col gap-1">
        <span className={ROTULO}>Ficheiros recebidos ({recebidos.length})</span>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => recebidosRef.current?.click()} className={BTN}>
            <FolderOpen size={11} /> Juntar do disco
          </button>
          <label className="flex items-center gap-1 text-[var(--color-text-muted)]">
            <input type="checkbox" checked={usarAbertos} onChange={(e) => setUsarAbertos(e.target.checked)} className="accent-[var(--color-accent)]" />
            Incluir os modelos abertos ({models.length})
          </label>
          {recebidos.length > 0 && (
            <button type="button" onClick={() => setRecebidos([])} className="text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]">
              Limpar
            </button>
          )}
        </div>
        <input ref={recebidosRef} type="file" multiple className="hidden" onChange={(e) => void escolherRecebidos(e.target.files)} />
      </div>
      <div>
        <button type="button" onClick={() => void verificar()} disabled={!termo || aLer !== null} className={BTN_PRIMARIO}>
          Verificar
        </button>
      </div>
      {aLer && (
        <p className={NOTA}>
          <Loader2 size={10} className="mr-1 inline animate-spin" />
          {aLer}
        </p>
      )}
      {erro && <p className={ERRO}>{erro}</p>}
      {resultado && <ResultadoDaVerificacao r={resultado} />}
    </>
  )
}

function Sinal({ ok, children }: { ok: boolean | null; children: React.ReactNode }) {
  if (ok === null) return <p className={AVISO}>{children}</p>
  return (
    <p className={ok ? OK : ERRO}>
      {ok ? <CheckCircle2 size={10} className="mr-1 inline" /> : <XCircle size={10} className="mr-1 inline" />}
      {children}
    </p>
  )
}

function dataLegivel(iso: string | null): string {
  if (!iso) return 'não indicada'
  return iso.replace('T', ' ').replace(/:\d{2}(\.\d+)?Z$/, ' UTC')
}

function ResultadoDaVerificacao({ r }: { r: Resultado }) {
  const assinaturas =
    r.origem === 'pdf'
      ? r.assinaturasPdf.map((a) => ({
          chave: `pdf-${a.ordem}`,
          assinante: a.cms?.assinante ?? null,
          emissor: a.cms?.emissor ?? null,
          data: a.cms?.dataDeclarada ?? null,
          validade: a.cms ? `${dataLegivel(a.cms.certificadoValidoDe)} a ${dataLegivel(a.cms.certificadoValidoAte)}` : null,
          formato: a.formato ?? 'desconhecido',
          integra: a.integra,
          erro: a.erro,
          nota: a.cobreAteAoFim
            ? null
            : `Depois desta assinatura há mais ${a.bytesDepois} bytes no ficheiro: pode ser outra assinatura ou informação de validação acrescentada pelo assinador, ou uma alteração. Confirmar no Autenticação.gov.`,
        }))
      : (r.asic?.assinaturas ?? []).map((a, i) => ({
          chave: `asic-${i}`,
          assinante: a.certificado?.sujeito ?? null,
          emissor: a.certificado?.emissor ?? null,
          data: a.data,
          validade: a.certificado ? `${dataLegivel(a.certificado.validoDe)} a ${dataLegivel(a.certificado.validoAte)}` : null,
          formato: `${a.formato} (${a.ficheiro})`,
          integra: a.integra,
          erro: a.erro,
          nota: null,
        }))

  return (
    <div className="flex flex-col gap-2 border-t border-[var(--color-border)] pt-2" data-campo="resultado-da-verificacao">
      <span className={ROTULO}>Assinaturas em {r.nome}</span>
      {assinaturas.length === 0 && <Sinal ok={false}>O ficheiro não tem assinatura digital.</Sinal>}
      {assinaturas.map((a) => (
        <div key={a.chave} className="rounded border border-[var(--color-border)] p-2">
          <div className="text-[var(--color-text)]">
            <span className="font-medium">{a.assinante ?? 'Assinante não identificado'}</span>
          </div>
          <div className={NOTA}>
            Emitido por {a.emissor ?? 'não identificado'} · data declarada {dataLegivel(a.data)}
            {a.validade ? ` · certificado válido de ${a.validade}` : ''} · {a.formato}
          </div>
          {a.erro ? (
            <Sinal ok={null}>Não foi possível ler esta assinatura ({a.erro}). Confirmar no Autenticação.gov.</Sinal>
          ) : (
            <Sinal ok={a.integra}>
              {a.integra === true
                ? r.origem === 'asic' && a.formato.startsWith('XAdES')
                  ? 'Os ficheiros do contentor são exactamente os que a assinatura refere.'
                  : 'O conteúdo assinado está intacto.'
                : a.integra === false
                  ? 'O conteúdo foi alterado depois de assinado.'
                  : 'A integridade não pôde ser conferida aqui.'}
            </Sinal>
          )}
          {a.nota && <p className={AVISO}>{a.nota}</p>}
        </div>
      ))}

      {r.origem === 'pdf' && !r.manifesto && (
        <p className={AVISO}>
          Este PDF não foi gerado pelo hub: não traz a lista de ficheiros em formato legível, por isso não há comparação
          automática. As assinaturas acima leem-se na mesma.
        </p>
      )}
      {r.manifesto && (
        <>
          <span className={ROTULO}>Termo</span>
          <div className={NOTA}>
            {r.manifesto.projecto} · {r.manifesto.entrega} · emitido por {r.manifesto.emitidoPor} para {r.manifesto.destinatario} ·{' '}
            {r.manifesto.data}
          </div>
          {r.manifestoAssinado === false && <Sinal ok={false}>A lista de ficheiros não está dentro da parte assinada.</Sinal>}
          {r.manifestoAssinado === true && r.origem === 'asic' && <Sinal ok>O termo está entre os ficheiros assinados do contentor.</Sinal>}
        </>
      )}
      {r.origem === 'asic' && r.referenciasAsic.length > 0 && (
        <>
          <span className={ROTULO}>Ficheiros no contentor</span>
          {r.referenciasAsic.map((x) => (
            <Sinal key={x.nome} ok={x.estado === 'confere' ? true : x.estado === 'diferente' ? false : null}>
              {x.nome}:{' '}
              {x.estado === 'confere' ? 'confere com o assinado' : x.estado === 'diferente' ? 'diferente do assinado' : 'não referido pela assinatura'}
            </Sinal>
          ))}
        </>
      )}
      {r.comparacao && (
        <>
          <span className={ROTULO}>Ficheiros recebidos</span>
          {r.comparacao.linhas.map((l) => (
            <Sinal key={`${l.esperado.nome}-${l.esperado.sha256}`} ok={l.estado === 'igual' || l.estado === 'igual-outro-nome' ? true : l.estado === 'diferente' ? false : null}>
              {l.esperado.nome}: {ROTULO_DO_ESTADO[l.estado]}
              {l.estado === 'igual-outro-nome' && l.recebido ? ` (${l.recebido.nome})` : ''}
            </Sinal>
          ))}
          {r.comparacao.aMais.length > 0 && (
            <p className={NOTA}>Fora da entrega: {r.comparacao.aMais.map((x) => x.nome).join(', ')}.</p>
          )}
        </>
      )}
      {r.manifesto && !r.comparacao?.linhas.some((l) => l.estado !== 'em-falta') && (
        <p className={NOTA}>Juntar os ficheiros recebidos (do disco ou abertos no viewer) para os comparar com o termo.</p>
      )}
    </div>
  )
}
